import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { ZkIOption } from '@zenklub/ng-ui/component/input/dropdown/dropdown.interface';
import {
	ICNPJValidation,
	IProfessionalFinancial,
	IProfessionalPerson,
} from 'app/interfaces/professionalprofile.interface';
import { CacheService } from 'app/services/cache.service';
import { ErrorService } from 'app/services/error.service';
import { EventsService } from 'app/services/events.service';
import { IdWallService } from 'app/services/id-wall.service';
import { UserService } from 'app/services/user.service';
import { timer } from 'rxjs';

@Component({
	selector: 'app-company-validation',
	templateUrl: './companyValidation.component.html',
	styleUrls: ['./companyValidation.component.scss'],
})
export class CompanyValidationComponent implements OnChanges, OnInit {
	@Input() formParams: FormGroup;
	@Input() showTitle = true;
	@Input() professionalPerson: IProfessionalPerson;
	@Output() waitingIdwallResponse = new EventEmitter<boolean>();
	@Output() cpnjValidationTrigger = new EventEmitter<string>();
	@Output() statusProfessional = new EventEmitter<string>();
	serviceCodeOptions: ZkIOption[];
	onValidationSuccess: boolean;
	validationRequired: boolean;
	isValidationTakingWhile = false;
	responseValidation: IProfessionalFinancial;
	idwallDebounce: NodeJS.Timeout;
	professionalFinancial: IProfessionalFinancial[];
	isChanging = true;
	canSendData = false;
	isCNAEError = false;
	isCNPJValid: boolean;
	validationLoadingOrSuccess = false;
	initialFormParams = { CPF: '', CNPJ: '' };

	idWallError: string;

	CNAEAproved = [
		'8650-0/03',
		'8599-6/04',
		'8219-9/99',
		'7490-1/99',
		'8690-9/01',
	];

	constructor(
		public idWallService: IdWallService,
		private events: EventsService,
		protected notifications: ZkNotificationService,
		protected translateService: TranslateService,
		protected errorService: ErrorService,
		private cacheService: CacheService,
		private userService: UserService
	) {}

	ngOnInit(): void {
		this.events.getCNPJValidation().subscribe(() => {
			this.showResponseValidation();
		});

		this.validateCNPJ();

		this.initialFormParams = {
			CPF: this.formParams.get('fiscalId').value,
			CNPJ: this.formParams.get('businessFiscalId').value,
		};
	}

	public ngOnChanges() {
		this.showResponseValidation();
	}

	public triggerValidation(professional) {
		this.statusProfessional.emit();
		this.cpnjValidationTrigger.emit(professional);
	}

	public validateCNPJ() {
		if (this.professionalPerson?.idWallReportStatus) {
			if (this.professionalPerson.idWallReportStatus === 'APROVADO') {
				this.onValidationSuccess = true;
				this.isValidationTakingWhile = false;
			}

			if (this.professionalPerson.idWallReportStatus === 'REJEITADO') {
				this.canSendData = false;
				this.isValidationTakingWhile = false;
				this.idWallErrorMsg(this.professionalPerson.errorCode);

				if (this.professionalPerson.errorCode === 3) {
					this.isCNAEError = true;
				}
			}

			if (this.professionalPerson.idWallReportStatus === 'PENDENTE') {
				this.validationRequired = true;
				this.isValidationTakingWhile = true;
			}
		}

		if (this.isValidationTakingWhile || this.onValidationSuccess) {
			this.validationLoadingOrSuccess = true;
		}
	}

	isFieldInvalid(fieldName: string): boolean {
		const field = this.formParams?.get(fieldName);

		return (field.invalid && field.dirty) || false;
	}

	async showResponseValidation() {
		const idwallResponse = this.cacheService.getStore('idwallResponse');
		this.professionalFinancial = idwallResponse?.data;
		this.waitingIdwallResponse.emit(false);

		if (this.professionalFinancial && this.professionalFinancial?.length >= 1) {
			const professionalUpdated = await this.getProfessionalPerson();

			this.triggerValidation(professionalUpdated);
			this.isValidationTakingWhile = false;

			if (this.professionalFinancial[0].status === 'APROVADO') {
				this.onValidationSuccess = true;
			}
			if (this.professionalFinancial[0].status === 'REJEITADO') {
				this.canSendData = false;
				this.onValidationSuccess = false;

				this.idWallErrorMsg(this.professionalFinancial[0].errorCode);

				if (this.professionalFinancial[0].errorCode === 3) {
					this.isCNAEError = true;
				}
			}
			this.validationRequired = false;
		}
	}

	idWallErrorMsg(id) {
		switch (id) {
			case 0:
				this.idWallError = this.translateService.instant(
					'MY_ACCOUNT.FINANCIAL.BILLING_ADDRESS.ERROR.CPNJ_INCOMPLET'
				);
				break;
			case 1:
				this.idWallError = this.translateService.instant(
					'MY_ACCOUNT.FINANCIAL.BILLING_ADDRESS.ERROR.CNPJ_INVALID'
				);
				break;
			case 2:
				this.idWallError = this.translateService.instant(
					'MY_ACCOUNT.FINANCIAL.BILLING_ADDRESS.ERROR.CPF_NOT_OWNER'
				);
				break;
			case 3:
				this.idWallError = this.translateService.instant(
					'MY_ACCOUNT.FINANCIAL.BILLING_ADDRESS.ERROR.CNAE_INVALID'
				);

				break;
			case 4:
				this.idWallError = this.translateService.instant(
					'MY_ACCOUNT.FINANCIAL.BILLING_ADDRESS.ERROR.CPNJ_INCOMPLET'
				);
		}
	}

	getProfessionalPerson(): Promise<IProfessionalPerson> {
		return new Promise(async (resolve) => {
			this.userService.checkProfessionalPerson().subscribe((result) => {
				return resolve(result);
			});
		});
	}

	public listenFormChanges(): void {
		this.formParams?.controls?.businessFiscalId?.valueChanges.subscribe(() => {
			this.onValidationSuccess = false;
		});
	}

	public onRequestValidation() {
		if (!this.canSendData || this.validationRequired) {
			return; /** To bypass freud-button bug in v2.4.1: click even if disabled */
		}

		this.onValidationSuccess = false;
		this.validationRequired = !this.validationRequired;
		this.waitingIdwallResponse.emit(true);

		const newCNPJValidation: ICNPJValidation = {
			businessFiscalId: this.formParams.controls.businessFiscalId.value,
			fiscalId: this.formParams.controls.fiscalId.value,
		};

		try {
			this.idWallService.cpnjValidation(newCNPJValidation).subscribe(
				async () => {
					this.showLoadingMessage();
				},
				(response) => {
					this.validationRequired = !this.validationRequired;

					if (response.error.type === 'joi/fiscalId/string.empty') {
						return this.notifications.showError(
							this.translateService.instant(
								'MY_ACCOUNT.FINANCIAL.BILLING_DATA.ERROR.FISCAL_ID'
							)
						);
					}

					this.notifications.showError(
						this.translateService.instant(
							'MY_ACCOUNT.FINANCIAL.BILLING_DATA.ERROR.TRY_AGAIN'
						),
						this.translateService.instant(
							'MY_ACCOUNT.FINANCIAL.BILLING_DATA.ERROR.CANT_VALIDATE'
						)
					);
				}
			);
		} catch (error) {
			return error;
		}
	}

	showLoadingMessage(): void {
		timer(5000).subscribe(() => {
			this.isValidationTakingWhile = true;
		});
	}

	changeInputIdwall(): void {
		const newCPF = this.formParams.get('fiscalId').value;
		const newCNPJ = this.formParams.get('businessFiscalId').value;

		const newData =
			this.initialFormParams.CPF != newCPF ||
			this.initialFormParams.CNPJ != newCNPJ;

		const someInvalidField =
			this.isFieldInvalid('fiscalId') ||
			this.isFieldInvalid('businessFiscalId');

		if (newData && !someInvalidField) {
			this.idWallError = null;
			this.canSendData = true;
			this.isChanging = false;
			this.isCNAEError = false;
		}
	}
}
